<!--
 * Path: packages/frontend/src/features/browser/forms/MultiselectBlock.vue
 * Task: Wrapper component for multiselect layouts implementing FormBlockWrapper.
 * Refactoring: Eliminated dependency on editorStore, mapping perfectly to pure composables.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('show-info')"
  >
    <template #read>
      <div v-if="displayItems.length === 0" class="text-body-2 text-disabled ms-1">
        -
      </div>
      <div v-else class="multiselect-grid-layout">
        <EntityPanel
          v-for="item in displayItems"
          :key="item.id || Math.random()"
          :entity="item"
          :is-link="true"
          :in-right-pane="formContext?.isSecondary || false"
        />
      </div>
    </template>

    <template #edit>
      <Multiselect :field="field" @show-info="$emit('show-info')" class="mb-0" />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import Multiselect from '@/core/components/forms/Multiselect.vue';
import EntityPanel from '@/core/components/common/EntityPanel.vue';

interface Props {
  field: any;
  inverseColumns?: any[];
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'show-info'): void }>();

const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const { getSelectOptions } = useSelectOptions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);
const activeEntity = computed(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

// --- Grid Generation Logic ---
const resolvedMultiselectItems = computed(() => {
  if (!activeEntity.value) return [];
  const ids = (activeEntity.value as any)[props.field.id];
  if (!Array.isArray(ids)) return [];

  const selectOptions = getSelectOptions(props.field.entitySource || '', props.field.id) || [];

  return ids.map((id: string | number) => {
    // Look it up in the actual complete datastore instead of filtered lists
    const entity = datastoreStore.allEntities.find((e: Entity) => e.id === id);
    if (entity) return entity;

    const foundOption = selectOptions.find((opt: any) => String(opt.id) === String(id));
    return {
      id: String(id),
      title: foundOption ? foundOption.title : String(id),
      color: foundOption?.color,
      icon: foundOption?.icon
    } as Partial<Entity>;
  }).sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''));
});

// Merges default relations or inverted relations (Incoming) to a uniform item list
const displayItems = computed<Partial<Entity>[]>(() => {
  if (props.inverseColumns && props.inverseColumns.length > 0) {
    return props.inverseColumns[0].items || [];
  }
  return resolvedMultiselectItems.value;
});
</script>

<style scoped>
.multiselect-grid-layout {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
  width: 100%;
}
</style>
